import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { 
  initGA, 
  storeUTMParameters, 
  logGAStatus,
  isGALoaded 
} from '../utils/analytics';

interface AnalyticsContextType {
  isLoaded: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  isLoaded: false,
});

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [isLoaded, setIsLoaded] = React.useState(false);

  useEffect(() => {
    // Initialize Google Analytics
    const initializeAnalytics = () => {
      try {
        // Store UTM parameters on page load
        storeUTMParameters();
        
        // Initialize GA
        initGA();
        
        // Check if GA is loaded
        const loaded = isGALoaded();
        setIsLoaded(loaded);
        
        if (loaded) {
          // Initial page view is handled by usePageTracking on first route render.
          // Log GA status in development
          logGAStatus();
          console.log('Google Analytics initialized successfully');
        } else {
          console.warn('Google Analytics not initialized - check REACT_APP_GA_TRACKING_ID environment variable');
        }
      } catch (error) {
        console.error('Failed to initialize Google Analytics:', error);
      }
    };

    // Initialize immediately or wait for gtag to be available
    if (typeof window !== 'undefined') {
      if (typeof window.gtag === 'function') {
        initializeAnalytics();
      } else {
        // Wait for GA script to load
        const checkGtag = setInterval(() => {
          if (typeof window.gtag === 'function') {
            clearInterval(checkGtag);
            initializeAnalytics();
          }
        }, 100);

        // Cleanup interval after 10 seconds if gtag doesn't load
        setTimeout(() => {
          clearInterval(checkGtag);
          if (typeof window.gtag !== 'function') {
            console.warn('Google Analytics gtag not loaded after 10 seconds');
          }
        }, 10000);
      }
    }
  }, []);

  return (
    <AnalyticsContext.Provider value={{ isLoaded }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Hook to use analytics
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};